import { Badge, Button, Card, CardContent, Table, TableColumn } from '@ibsys/kit';
import { format } from 'date-fns';
import { FileIcon, FileQuestionIcon, FileSpreadsheetIcon, FileTextIcon, PresentationIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KNOWLEDGE_SOURCE_FILTER_PARAM } from '@/entities/knowledge';
import { KnowledgeSourceListItem, KnowledgeSourceType, useLazyKnowledgeSources } from '@/entities/knowledgeSources';
import { PredicateInput } from '@/features/PredicateInput';
import { useTableState } from '@/entities/settings';
import { DOWNLOAD_FORMAT_BADGE_CLASS } from '@/shared/lib';
import { useAppNavigate } from '@/shared/lib/ts-router';
import { Icon, PageContainer } from '@/shared/ui';

type FileTypeConfig = {
  icon: React.ReactNode;
  /** Оформление лейбла формата; у типа без своего формата скачивания лейбл нейтральный */
  badgeClass?: string;
};

// Помеченные типы файлов, для которых задано специальное оформление и название
const KNOWN_FILE_TYPES = ['pdf', 'word', 'excel', 'powerpoint'] as const;

type KnownFileType = (typeof KNOWN_FILE_TYPES)[number];

// Тип источника входит в набор помеченных
const isKnownFileType = (sourceType: KnowledgeSourceType): sourceType is KnownFileType =>
  (KNOWN_FILE_TYPES as readonly string[]).includes(sourceType);

// Иконка нейтрального цвета, цвет лейбла различает тип файла.
// Цвет берётся из общего оформления форматов {@link DOWNLOAD_FORMAT_BADGE_CLASS}: тип источника
// помечается так же, как формат при скачивании. Для типа вне этого перечня лейбл нейтральный
const fileTypeConfig: Record<KnownFileType, FileTypeConfig> = {
  pdf: {
    icon: <FileTextIcon className="size-4 shrink-0 text-muted-foreground" />,
    badgeClass: DOWNLOAD_FORMAT_BADGE_CLASS.pdf,
  },
  word: {
    icon: <FileIcon className="size-4 shrink-0 text-muted-foreground" />,
    badgeClass: DOWNLOAD_FORMAT_BADGE_CLASS.docx,
  },
  excel: {
    icon: <FileSpreadsheetIcon className="size-4 shrink-0 text-muted-foreground" />,
    badgeClass: DOWNLOAD_FORMAT_BADGE_CLASS.xlsx,
  },
  powerpoint: {
    icon: <PresentationIcon className="size-4 shrink-0 text-muted-foreground" />,
  },
};

// Нейтральный запасной маркер для типов вне помеченного набора (document, unknown)
const fallbackFileTypeConfig: FileTypeConfig = {
  icon: <FileQuestionIcon className="size-4 shrink-0 text-muted-foreground" />,
};

// Оформление типа источника; нераспознанный тип получает нейтральный запасной маркер вместо сбоя
const getFileTypeConfig = (sourceType: KnowledgeSourceType): FileTypeConfig =>
  isKnownFileType(sourceType) ? fileTypeConfig[sourceType] : fallbackFileTypeConfig;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const KnowledgeSourcesPage: FC = () => {
  const { t } = useTranslation(['knowledgeSources', 'common']);

  const { refetch: fetchData } = useLazyKnowledgeSources();
  const { settings, actions, path } = useTableState('knowledgeSourcesList');
  const navigate = useAppNavigate();

  // Опции фильтра по типу — из единого набора помеченных типов, без отдельного захардкоженного списка.
  // Нераспознанные типы (document, unknown) в фильтр не выводятся: серверная фильтрация их не поддерживает
  const sourceTypeOptions = useMemo(
    () =>
      KNOWN_FILE_TYPES.map((type) => ({
        value: type,
        label: t(`knowledgeSources:fileTypes.${type}`),
      })),
    [t],
  );

  const handleGoToNodes = useCallback(
    (sourceId: number) => {
      navigate('/knowledge', undefined, { query: { [KNOWLEDGE_SOURCE_FILTER_PARAM]: sourceId } });
    },
    [navigate],
  );

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'fileName',
          header: t('knowledgeSources:table.fileName'),
          size: 250,
          minSize: 150,
          cell: ({ row }) => (
            <div className="flex items-start gap-2">
              {getFileTypeConfig(row.original.sourceType).icon}
              <span className="line-clamp-2 break-all">{row.original.file.fileName}</span>
            </div>
          ),
        },
        {
          accessorKey: 'sourceType',
          header: t('knowledgeSources:table.type'),
          minSize: 100,
          maxSize: 150,
          filters: {
            type: 'select',
            options: sourceTypeOptions,
          },
          cell: ({ row }) => {
            const { sourceType } = row.original;
            // Помеченному типу — свой перевод, нераспознанному — нейтральное обозначение
            const typeLabel = isKnownFileType(sourceType)
              ? t(`knowledgeSources:fileTypes.${sourceType}`)
              : t('knowledgeSources:fileTypes.other');
            return (
              <Badge variant="secondary" outline className={getFileTypeConfig(sourceType).badgeClass}>
                {typeLabel}
              </Badge>
            );
          },
        },
        {
          accessorKey: 'size',
          header: t('knowledgeSources:table.size'),
          minSize: 80,
          maxSize: 120,
          cell: ({ row }) => formatFileSize(row.original.file.size),
        },
        {
          accessorKey: 'createdAt',
          header: t('knowledgeSources:table.uploadDate'),
          minSize: 120,
          maxSize: 160,
          enableSorting: true,
          cell: ({ row }) => format(new Date(row.original.createdAt), 'dd.MM.yyyy'),
        },
        {
          header: '',
          accessorKey: 'id',
          minSize: 80,
          maxSize: 100,
          enableResizing: false,
          pinned: 'right',
          cell: ({ row }) => (
            <div className="flex w-full justify-end gap-2">
              <Button
                size="icon"
                className={'size-6'}
                icon={<Icon type="arrow-long-right" className={'size-3'} />}
                variant="secondary"
                aria-label={t('knowledgeSources:goToNodes')}
                tooltip={t('knowledgeSources:goToNodes')}
                onClick={() => handleGoToNodes(row.original.id)}
              />
            </div>
          ),
        },
      ] satisfies TableColumn<KnowledgeSourceListItem>[],
    [t, sourceTypeOptions, handleGoToNodes],
  );

  return (
    <PageContainer enableScroll={false} className="flex h-full w-full flex-col gap-4 overflow-hidden pb-4">
      <header className="flex w-full items-center gap-4">
        <PredicateInput type="input" className="w-full" placeholder={t('common:search')} disabled />
      </header>
      <Card className="flex flex-1 flex-col gap-0 overflow-hidden">
        <CardContent className="h-full flex-1 overflow-hidden rounded-xl p-0">
          <Table
            type="lazy"
            limit={50}
            fetchData={fetchData}
            dataKey="items"
            virtualization={{ enable: true, rowHeight: 40 }}
            styles={{
              size: 'sm',
            }}
            classNames={{
              header: '[&_th]:h-14',
              table: 'min-w-full',
            }}
            className="h-full w-full border-0"
            enableMultiSort={false}
            {...settings}
            {...actions}
            tableStateStoragePath={`${path}.ordering`}
            enableColumnOrdering
            enableColumnSizing
            initialOffset={undefined}
            columns={columns}
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
};
